import React, { useState, useRef } from 'react';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { doc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import Swal from 'sweetalert2';

interface Certification {
  id: string;
  nombre: string;
  tipo: string;
  fecha: any;
  url: string;
  formato: string;
  fileName: string;
}

interface CertificationsManagerProps {
  userId: string;
  userType: 'asesor' | 'asociado';
  certificaciones: Certification[];
  onUpdate?: () => void;
  readOnly?: boolean;
}

export default function CertificationsManager({
  userId,
  userType,
  certificaciones = [],
  onUpdate,
  readOnly = false
}: CertificationsManagerProps) {
  const [uploading, setUploading] = useState(false);
  const [certName, setCertName] = useState('');
  const [certType, setCertType] = useState('Certificación');
  const [certDate, setCertDate] = useState(new Date().toISOString().split('T')[0]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const storage = getStorage();

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar nombre
    if (!certName.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Nombre requerido',
        text: 'Por favor ingresa un nombre para la certificación antes de subir el archivo.',
        confirmButtonColor: '#3085d6'
      });
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Validar tipo de archivo (PDF, JPG, PNG)
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      Swal.fire({
        icon: 'error',
        title: 'Formato no válido',
        text: 'Solo se permiten archivos PDF, JPG o PNG.',
        confirmButtonColor: '#d33'
      });
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Validar tamaño (máximo 10MB)
    if (file.size > 10 * 1024 * 1024) {
      Swal.fire({
        icon: 'error',
        title: 'Archivo muy grande',
        text: 'El archivo no debe superar los 10MB.',
        confirmButtonColor: '#d33'
      });
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    try {
      setUploading(true);

      // Generar ID único para la certificación
      const certId = `cert_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop();
      const fileName = `${userType}/${userId}/certificaciones/${certId}.${fileExtension}`;
      const storageRef = ref(storage, fileName);

      // Subir archivo a Firebase Storage
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      // Crear objeto de certificación
      const newCertification: Certification = {
        id: certId,
        nombre: certName.trim(),
        tipo: certType,
        fecha: new Date(certDate).toISOString(),
        url: downloadURL,
        formato: file.type.includes('pdf') ? 'pdf' : 'image',
        fileName: fileName
      };

      // Actualizar Firestore
      const docRef = doc(db, userType, userId);
      await updateDoc(docRef, {
        certificaciones: arrayUnion(newCertification)
      });

      // Limpiar formulario
      setCertName('');
      setCertType('Certificación');
      setCertDate(new Date().toISOString().split('T')[0]);
      if (fileInputRef.current) fileInputRef.current.value = '';

      Swal.fire({
        icon: 'success',
        title: '¡Éxito!',
        text: 'Certificación agregada correctamente.',
        confirmButtonColor: '#3085d6'
      });

      // Callback para refrescar datos
      if (onUpdate) onUpdate();

    } catch (error) {
      console.error('Error al subir certificación:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error al subir la certificación. Inténtalo de nuevo.',
        confirmButtonColor: '#d33'
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (certification: Certification) => {
    const result = await Swal.fire({
      title: '¿Eliminar certificación?',
      text: `¿Estás seguro de eliminar "${certification.nombre}"? Esta acción no se puede deshacer.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (!result.isConfirmed) return;

    try {
      // Eliminar archivo de Storage
      if (certification.fileName) {
        try {
          const storageRef = ref(storage, certification.fileName);
          await deleteObject(storageRef);
        } catch (storageError) {
          console.warn('No se pudo eliminar el archivo de Storage:', storageError);
        }
      }

      // Eliminar de Firestore
      const docRef = doc(db, userType, userId);
      await updateDoc(docRef, {
        certificaciones: arrayRemove(certification)
      });

      Swal.fire({
        icon: 'success',
        title: '¡Eliminada!',
        text: 'La certificación ha sido eliminada.',
        confirmButtonColor: '#3085d6'
      });

      // Callback para refrescar datos
      if (onUpdate) onUpdate();

    } catch (error) {
      console.error('Error al eliminar certificación:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error al eliminar la certificación. Inténtalo de nuevo.',
        confirmButtonColor: '#d33'
      });
    }
  };

  const handleDownload = (url: string, nombre: string) => {
    window.open(url, '_blank');
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch {
      return 'Fecha no disponible';
    }
  };

  return (
    <div className="certifications-manager">
      {/* Formulario de carga (solo si no es readOnly) */}
      {!readOnly && (
        <div className="upload-section mb-4 p-4 border rounded bg-light">
          <h6 className="mb-3">
            <i className="fa-solid fa-upload me-2"></i>
            Agregar Nueva Certificación
          </h6>

          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label">
                <strong>Nombre de la Certificación *</strong>
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="Ej: Certificación NAR, Licencia AMPI, etc."
                value={certName}
                onChange={(e) => setCertName(e.target.value)}
                disabled={uploading}
                maxLength={100}
              />
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label">
                <strong>Tipo *</strong>
              </label>
              <select
                className="form-select"
                value={certType}
                onChange={(e) => setCertType(e.target.value)}
                disabled={uploading}
              >
                <option value="Certificación">Certificación</option>
                <option value="Licencia">Licencia</option>
                <option value="Capacitación">Capacitación</option>
                <option value="Diploma">Diploma</option>
                <option value="Título">Título</option>
                <option value="Otro">Otro</option>
              </select>
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label">
                <strong>Fecha de Obtención *</strong>
              </label>
              <input
                type="date"
                className="form-control"
                value={certDate}
                onChange={(e) => setCertDate(e.target.value)}
                disabled={uploading}
                max={new Date().toISOString().split('T')[0]}
              />
            </div>

            <div className="col-md-12">
              <label className="form-label">
                <strong>Archivo (PDF, JPG, PNG - Máx. 10MB) *</strong>
              </label>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept=".pdf,.jpg,.jpeg,.png"
                className="form-control"
                disabled={uploading}
              />
              <small className="text-muted">
                Formatos permitidos: PDF, JPG, PNG. Tamaño máximo: 10MB
              </small>
            </div>
          </div>

          {uploading && (
            <div className="text-center mt-3">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Subiendo...</span>
              </div>
              <p className="mt-2 text-muted">Subiendo certificación...</p>
            </div>
          )}
        </div>
      )}

      {/* Lista de certificaciones */}
      <div className="certifications-list">
        <h6 className="mb-3">
          <i className="fa-solid fa-certificate me-2"></i>
          Certificaciones Actuales {certificaciones.length > 0 && `(${certificaciones.length})`}
        </h6>

        {certificaciones.length === 0 ? (
          <div className="text-center py-5">
            <i className="fa-solid fa-certificate fa-4x text-muted mb-3"></i>
            <p className="text-muted">
              {readOnly
                ? 'No hay certificaciones registradas.'
                : 'No has agregado certificaciones aún. Completa el formulario arriba para agregar tu primera certificación.'}
            </p>
          </div>
        ) : (
          <div className="row">
            {certificaciones.map((cert) => (
              <div key={cert.id} className="col-md-6 col-lg-4 mb-3">
                <div className="card h-100 shadow-sm">
                  {/* Preview */}
                  <div className="card-img-top bg-light d-flex align-items-center justify-content-center" style={{ height: '150px' }}>
                    {cert.formato === 'pdf' ? (
                      <i className="fa-solid fa-file-pdf fa-5x text-danger"></i>
                    ) : (
                      <img
                        src={cert.url}
                        alt={cert.nombre}
                        style={{ maxHeight: '150px', maxWidth: '100%', objectFit: 'contain' }}
                      />
                    )}
                  </div>

                  {/* Información */}
                  <div className="card-body">
                    <h6 className="card-title text-truncate" title={cert.nombre}>
                      {cert.nombre}
                    </h6>
                    <p className="card-text mb-1">
                      <small className="text-muted">
                        <i className="fa-solid fa-tag me-1"></i>
                        {cert.tipo}
                      </small>
                    </p>
                    <p className="card-text mb-2">
                      <small className="text-muted">
                        <i className="fa-solid fa-calendar me-1"></i>
                        {formatDate(cert.fecha)}
                      </small>
                    </p>

                    {/* Botones */}
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-sm btn-primary flex-grow-1"
                        onClick={() => handleDownload(cert.url, cert.nombre)}
                        title="Ver certificación"
                      >
                        <i className="fa-solid fa-eye me-1"></i>
                        Ver
                      </button>
                      {!readOnly && (
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDelete(cert)}
                          title="Eliminar certificación"
                        >
                          <i className="fa-solid fa-trash"></i>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Estilos adicionales */}
      <style>{`
        .certifications-manager .form-label strong {
          font-weight: 600;
        }
        .certifications-manager .card {
          transition: transform 0.2s;
        }
        .certifications-manager .card:hover {
          transform: translateY(-5px);
        }
      `}</style>
    </div>
  );
}
